"use client"

import { useState, useEffect } from "react"
import { useLanguage } from "./LanguageContext"
import { loadAppConfiguration } from "./ContentLoader"
import { usePasswordCheck } from "./hooks/usePasswordCheck"
import { useSeo } from "./hooks/useSeo"
import Navigation, { Footer } from "./Navigation"
import "./css/AboutPage.css"
import "./css/ResourcesPage.css"

type ArticleCard = {
  slug: string
  title: { nl?: string; en?: string }
  excerpt: { nl?: string; en?: string }
  audience: string
  publishedAt: string
  stage?: "draft" | "published" | null
  lang?: "nl" | "en"
  isFallback?: boolean
}

const AUDIENCE_LABEL: Record<string, { nl: string; en: string }> = {
  ouders: { nl: "Voor ouders", en: "For parents" },
  leerkrachten: { nl: "Voor leerkrachten", en: "For teachers" },
  algemeen: { nl: "Algemeen", en: "General" },
}

const ResourcesPage = () => {
  const { language } = useLanguage()
  const { isAdmin, isLoading: authLoading } = usePasswordCheck()
  const [appConfig, setAppConfig] = useState<any>(null)
  const [articles, setArticles] = useState<ArticleCard[]>([])
  const [drafts, setDrafts] = useState<ArticleCard[]>([])
  const [isLoading, setIsLoading] = useState(true)

  const t = (path: string, defaultText = "") => {
    if (!appConfig) return defaultText
    const keys = path.split(".")
    let value: any = appConfig
    for (const key of keys) {
      if (!value || !value[key]) return defaultText
      value = value[key]
    }
    if (value && typeof value === "object" && value[language.code]) return value[language.code]
    return typeof value === "string" ? value : value?.en || defaultText
  }

  useSeo({
    path: "/ontdek",
    title:
      language.code === "en"
        ? "Discover: articles for parents and teachers | Techtok"
        : "Ontdek: artikelen voor ouders en leerkrachten | Techtok",
    description:
      language.code === "en"
        ? "Practical articles about raising digital makers: screen time, AI, creativity and digital literacy, from the team behind Techtok."
        : "Praktische artikelen over kinderen digitaal laten maken: schermtijd, AI, creativiteit en digitale geletterdheid, van het team achter Techtok.",
  })

  useEffect(() => {
    if (authLoading) return
    const load = async () => {
      setIsLoading(true)
      try {
        const config = await loadAppConfiguration(language.code, null, false)
        setAppConfig(config)

        const langQ = `lang=${language.code === "en" ? "en" : "nl"}`

        // All /ontdek articles live in the database now (campaign_creatives,
        // article_stage = 'published'). This endpoint only exists on a
        // PHP-enabled host, not the plain CRA dev server, so a failure here
        // shows an empty list rather than crashing the page.
        try {
          const apiRes = await fetch(`/portal/api/articles-public.php?${langQ}`)
          const contentType = apiRes.headers.get("content-type") || ""
          if (apiRes.ok && contentType.includes("application/json")) {
            const apiData = await apiRes.json()
            if (apiData?.success) {
              const sorted = [...(apiData.articles || [])].sort((a: ArticleCard, b: ArticleCard) =>
                b.publishedAt.localeCompare(a.publishedAt)
              )
              setArticles(sorted)
            }
          }
        } catch (e) {
          console.warn("Kon Ontdek-artikelen niet laden (portal niet bereikbaar):", e)
        }

        // Admins additionally see draft articles (article_stage = 'draft'),
        // served only by the session-gated endpoint.
        if (isAdmin) {
          try {
            const r = await fetch(`/portal/api/article-preview.php?list=drafts&${langQ}`, { credentials: "include" })
            const ct = r.headers.get("content-type") || ""
            if (r.ok && ct.includes("application/json")) {
              const d = await r.json()
              if (d?.success) setDrafts(d.articles || [])
            }
          } catch (e) {
            console.warn("Kon concept-artikelen niet laden:", e)
          }
        } else {
          setDrafts([])
        }
      } catch (e) {
        console.error("Kon Ontdek-artikelen niet laden:", e)
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [language.code, isAdmin, authLoading])

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    )
  }

  return (
    <div className="landing-selector-container">
      <Navigation appConfig={appConfig} currentPage="ontdek" onNavigate={() => {}} t={t} />

      <main className="main-content">
        <section className="resources-hero">
          <div className="resources-hero-inner">
            <h1 className="missies-page-title">{language.code === "nl" ? "Ontdek" : "Discover"}</h1>
            <p className="missies-page-subtitle">
              {language.code === "nl"
                ? "Artikelen en tips voor ouders en leerkrachten over digitale vaardigheden, apps maken en Techtok Missies."
                : "Articles and tips for parents and teachers on digital skills, building apps, and Techtok Missies."}
            </p>
          </div>
        </section>

        <section className="resources-grid-section">
          {articles.length === 0 && drafts.length === 0 ? (
            <p className="resources-empty">
              {language.code === "nl" ? "Binnenkort meer artikelen." : "More articles coming soon."}
            </p>
          ) : (
            <div className="resources-grid">
              {[...drafts, ...articles].map((article) => {
                const audience = AUDIENCE_LABEL[article.audience]
                const isDraft = article.stage === "draft"
                const onlyOtherLang = article.isFallback && article.lang
                return (
                  <a key={article.slug} href={`/ontdek/${article.slug}`} className="resource-card">
                    <span className="resource-card-tags">
                      {isDraft && <span className="resource-card-badge-draft">Concept</span>}
                      {onlyOtherLang && (
                        <span className="resource-card-badge-lang">
                          {article.lang === "en" ? "In English" : "In het Nederlands"}
                        </span>
                      )}
                      {audience && (
                        <span className="resource-card-audience">
                          {audience[language.code as "nl" | "en"] || audience.nl}
                        </span>
                      )}
                    </span>
                    <h2 className="resource-card-title">{article.title[language.code as "nl" | "en"] || article.title.nl}</h2>
                    <p className="resource-card-excerpt">{article.excerpt[language.code as "nl" | "en"] || article.excerpt.nl}</p>
                    <span className="resource-card-link">{language.code === "nl" ? "Lees verder" : "Read more"} →</span>
                  </a>
                )
              })}
            </div>
          )}
        </section>
      </main>

      <Footer appConfig={appConfig} t={t} />
    </div>
  )
}

export default ResourcesPage
